"use client";

import { useRouter } from "next/navigation";
import { createContext, useCallback, useContext, useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client/react";
import type { GetAuthenticatedItemQuery } from "@/__generated__/gql/graphql";

import { GET_AUTHENTICATED_ITEM } from "@/config/queries";
import { LOGIN_MUTATION, END_SESSION_MUTATION } from "@/config/mutation";

type AuthUser = NonNullable<GetAuthenticatedItemQuery["authenticatedItem"]>;

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthUser | null | undefined>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const router = useRouter();

  const {
    data,
    loading,
    refetch: refetchQuery
  } = useQuery(GET_AUTHENTICATED_ITEM, {
    fetchPolicy: "network-only"
  });

  const [authenticateMutation] = useMutation(LOGIN_MUTATION);
  const [endSessionMutation] = useMutation(END_SESSION_MUTATION);

  const user = data?.authenticatedItem ?? null;

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const { data } = await authenticateMutation({
          variables: { email, password }
        });

        const result = data?.authenticateUserWithPassword;

        if (result?.__typename === "UserAuthenticationWithPasswordSuccess") {
          await fetch("/api/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionToken: result.sessionToken })
          });
          const refetchResult = await refetchQuery();
          return refetchResult.data?.authenticatedItem;
        } else if (result?.__typename === "UserAuthenticationWithPasswordFailure") {
          throw new Error(result.message || "Authentication failed");
        }
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      }
    },
    [authenticateMutation, refetchQuery]
  );

  const logout = useCallback(async () => {
    try {
      await endSessionMutation();
      await fetch("/api/auth/logout", { method: "POST" });
      await refetchQuery();

      router.push("/login");
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  }, [endSessionMutation, refetchQuery, router]);

  const refetch = useCallback(async () => {
    await refetchQuery();
  }, [refetchQuery]);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      refetch
    }),
    [user, loading, login, logout, refetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
